import React, { useState, useEffect } from 'react';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import CaretButton from '@/components/common/libraries/common/CaretButton';
import styles from './Collapse.module.scss';

// AIDEV-NOTE: One caret glyph and colour everywhere (PLAT-17884) — a filter section header and a
// nested row differ only in size and side. Right means collapsed, down means expanded, in both.
// A section caret sits at the row's right edge; a row caret sits left of its checkbox, matching
// the Correlations panel.
type CaretVariant = 'section' | 'row';

const CARET_SIZE: Record<CaretVariant, 12 | 24> = { row: 12, section: 24 };

interface CollapseProps {
  caret?: CaretVariant;
  children?: React.ReactNode;
  shouldCollapse?: boolean;
  triggerElement?: JSX.Element;
}

const Collapse = ({
  caret = 'section',
  children,
  shouldCollapse,
  triggerElement,
}: CollapseProps) => {
  const [expanded, setExpanded] = useState(!shouldCollapse);

  useEffect(() => {
    setExpanded(!shouldCollapse);
  }, [shouldCollapse]);

  const caretButton = (
    <CaretButton
      aria-expanded={expanded}
      aria-label={`${expanded ? I18n.t('collapse') : I18n.t('expand')} ${I18n.t('section')}`}
      className={caret === 'row' ? styles.rowCaret : styles.collapseIcon}
      expanded={expanded}
      size={CARET_SIZE[caret]}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    />
  );

  return (
    <>
      <div
        className={cn(styles.collapseContainer, caret === 'row' && styles.row, 'body sm')}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Rendered before the trigger, not reordered with CSS, so the caret comes first for a
            screen reader and for tab order too. */}
        {caret === 'row' && caretButton}

        {triggerElement}

        {caret === 'section' && caretButton}
      </div>

      <div
        className={`${styles.collapsible} ${expanded ? '' : styles.collapsed}`}
      >
        {children}
      </div>
    </>
  );
};

export default Collapse;
